import React from 'react';
import clsx from 'clsx';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay, A11y } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/autoplay';

import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, {
    ContentColumn,
} from '@/layouts/ContentWrapper/ContentWrapper.component';
import Heading from '@/elements/Heading/Heading.component';
import Image from '@/elements/Image/Image.component';
import Icon from '@/elements/Icon/Icon.component';
import { IProps } from './LogoBlock.interface';
import './LogoBlock.styles.css';

const MultiRowLogoBlock: React.FC<IProps> = ({
    title,
    logos,
    className,
    ...blockWrapperProps
}) => {
    return (
        <BlockWrapper
            className={clsx('logoBlock', className, 'logoBlock--multiRow')}
            {...blockWrapperProps}
        >
            <ContentWrapper>
                <ContentColumn>
                    {title && (
                        <Heading level={2} className="logoBlock__title" html={title} />
                    )}
                    <div className="logoBlock__logos">
                        {logos.map((logo, index) => (
                            <div key={`logo-block-img-${index}`} className="logoBlock__item">
                                {logo.link && logo.image ? (
                                    <a {...logo.link} className="logoBlock__link">
                                        <Image {...logo.image} />
                                    </a>
                                ) : logo.image ? (
                                    <Image {...logo.image} />
                                ) : null}
                            </div>
                        ))}
                    </div>
                </ContentColumn>
            </ContentWrapper>
        </BlockWrapper>
    );
};

export const LogoBlock: React.FC<IProps> = ({
    title,
    logos,
    className,
    autoScroll = false,
    enableCarousel = false,
    ...blockWrapperProps
}) => {
    // Use grid layout when carousel is disabled
    if (!enableCarousel) {
        return (
            <MultiRowLogoBlock
                title={title}
                logos={logos}
                className={className}
                {...blockWrapperProps}
            />
        );
    }

    return (
        <BlockWrapper
            className={clsx('logoBlock', className)}
            {...blockWrapperProps}
        >
            <ContentWrapper>
                <ContentColumn>
                    {title && (
                        <Heading level={2} className="logoBlock__title" html={title} />
                    )}
                    <div className="logoBlock__swiperContainer">
                        <Swiper
                            className={clsx(
                                "logoBlock__swiper",
                                `logoBlock__swiper--logos-${logos.length}` // Used for responsive centering logic
                            )}
                            modules={[Navigation, Autoplay, A11y]}
                            rewind
                            // Enable centering for mobile scrolling - CSS overrides when all slides fit
                            centeredSlides={true}
                            centeredSlidesBounds={true}
                            initialSlide={0}
                            slidesOffsetBefore={0}
                            slidesOffsetAfter={0}
                            autoplay={autoScroll ? {
                                delay: 3000,
                                disableOnInteraction: false,
                                pauseOnMouseEnter: true,
                            }: false}
                            slidesPerView={Math.min(3, logos.length)}
                            slidesPerGroup={1}
                            spaceBetween={0} // No spacing - handled by internal slide padding
                            watchOverflow={true}
                            allowTouchMove={logos.length > 3}
                            breakpoints={{
                                // Tablet: 4 slides per view
                                512: {
                                    slidesPerView: Math.min(4, logos.length),
                                    slidesPerGroup: 1,
                                    spaceBetween: 0,
                                    allowTouchMove: logos.length > 4,
                                },
                                // Desktop: 5 slides per view  
                                768: {
                                    slidesPerView: Math.min(5, logos.length),
                                    slidesPerGroup: 1,
                                    spaceBetween: 0,
                                    allowTouchMove: logos.length > 5,
                                },
                                // Large desktop: 6 slides per view
                                1024: {
                                    slidesPerView: Math.min(6, logos.length),
                                    slidesPerGroup: 1,
                                    spaceBetween: 0,
                                    allowTouchMove: logos.length > 6,
                                },
                            }}
                            a11y={{ enabled: true }}
                            navigation={{
                                prevEl: '.logoBlock__navButton--prev',
                                nextEl: '.logoBlock__navButton--next',
                                hideOnClick: false,
                                disabledClass: 'swiper-button-disabled',
                                lockClass: 'swiper-button-lock',
                            }}
                        >
                            {logos.map((logo, index) => (
                                <SwiperSlide
                                    key={`logo-block-img-${index}`}
                                    className="logoBlock__slide"
                                >
                                    {logo.link && logo.image ? (
                                        <a {...logo.link} className="logoBlock__link">
                                            <Image {...logo.image} />
                                        </a>
                                    ) : logo.image ? (
                                        <Image {...logo.image} />
                                    ) : null}
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        {/* Navigation buttons - hidden responsively when all slides fit on screen */}
                        <div 
                            className={clsx(
                                "logoBlock__navigationContainer",
                                {
                                    [`logoBlock__navigationContainer--hide-mobile`]: logos.length <= 3,
                                    [`logoBlock__navigationContainer--hide-sm`]: logos.length <= 4,
                                    [`logoBlock__navigationContainer--hide-md`]: logos.length <= 5,
                                    [`logoBlock__navigationContainer--hide-lg`]: logos.length <= 6,
                                }
                            )}
                        >
                            <button
                                className="logoBlock__navButton logoBlock__navButton--prev"
                                aria-label="Previous logos"
                            >
                                <Icon icon="chevron-left" />
                            </button>
                            <button
                                className="logoBlock__navButton logoBlock__navButton--next"
                                aria-label="Next logos"
                            >
                                <Icon icon="chevron-right" />
                            </button>
                        </div>
                    </div>
                </ContentColumn>
            </ContentWrapper>
        </BlockWrapper>
    );
};
